import React, {useRef} from 'react'
import Select, {PromptType} from '../../../../Prompt/Select/Select'
import {NavigationButtons} from '../../../../Navigation/NavigationButtons/NavigatonButtons'
import Selector from '../../../../Selector/Selector'
import FieldMessage from '../../../../Form/FieldMessage'
import styled from 'styled-components'
import {_L, useLocaleState} from '../../../../../context/LocaleContext'
import Input from '../../../../Input/Input'
import {useUIState} from '../../../../../context/UIContext'
import {useUserState} from '../../../../../context/UserContext'
import Icons from '../../../../Icons'

const Instructions = ({instructions}) => {
  return (
    <InstructionsContainer>
      <InstructionsText>{instructions}</InstructionsText>
    </InstructionsContainer>
  )
}

const InstructionsContainer = styled.div`
  display: flex;
  justify-content: flex-start;
`
const InstructionsIcon = styled.div`
  margin-right: 12px;
`

const InstructionsText = styled.p`
  color: #3e4044;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
`

const Location = ({
  setValue,
  isQrRequest,
  parentState,
  setAction,
  onRoomSelect,
  visibleFields,
  instructions,
  title
}) => {
  const localeState = useLocaleState()
  const uiState = useUIState()
  const {defaultLocation} = useUserState()
  const {rm: userLocation} = defaultLocation
  const {
    errors,
    description,
    building,
    floor,
    room,
    location,
    equipment
  } = parentState
  const {isKiosk} = uiState

  const {hasLocation, hasEquipment, hasSpecificLocation} = visibleFields

  const parentEl = useRef(null)

  const onPromptChange = item => setValue(item)

  const onNext = evt => {	  
	    if(evt.key == "Enter" || evt.key == " " || !evt.key){ setAction({action: 'next'}) }
  }

  const onDescriptionChange = e =>
    setValue({
      description: e.target.value
    })

  const onLocationChange = e =>
    setValue({
      location: e.target.value
    })

  const getLocationInstructions = () => {
    const {value} = room
    const kioskLocationMessage = _L({
      key: 'This reflects your current location',
      localeState
    })
    const locationMessage = _L({
      key: 'This location reflects your current seat assignment',
      localeState
    })

    return value === userLocation.code
      ? isKiosk
        ? kioskLocationMessage
        : locationMessage
      : ''
  }

  const getLocationValue = () => {
    const {value, name} = room
    return value && name && name !== value ? `${value} – ${name}` : value
  }
  return (
    <StyledContainer ref={parentEl}>
      <Selector
        state={{building, floor, room}}
        onPromptChange={onPromptChange}
        render={(selectors, onChange) => (
          <StyledContent>
            <TopFieldContainer>
              <HeaderContainer>
                <StyledTitle>
                  <Title>{title}</Title>
                </StyledTitle>
              </HeaderContainer>
              {instructions && <Instructions instructions={instructions} />}
            </TopFieldContainer>
            <FieldContainer>
              <StyledFieldLabel htmlFor="Request Description">
                {_L({key: 'Request Description', localeState})}
              </StyledFieldLabel>
              <StyledTextArea
                id="Request Description"
                rows={4}
                onChange={onDescriptionChange}
                value={description}
                dataTestId="location-description"
              />
              <FieldMessage errors={errors} fieldName={'description'} />
              {hasLocation && (
                <div>
                  <Select
                    parentContainer={parentEl}
                    type={PromptType.BUILDING}
                    label={_L({key: 'Building', localeState})}
                    title={_L({key: 'Select a building', localeState})}
                    placeholder={_L({key: 'Search Buildings', localeState})}
                    value={building}
                    readOnly={isQrRequest}
                    selectors={selectors}
                    onChange={onChange}
                    dataTestId="location-building-select"
                    ariaLabel= "Select a building"
                  />
                  <FieldMessage errors={errors} fieldName={'building'} />

                  <Select
                    parentContainer={parentEl}
                    type={PromptType.FLOOR}
                    label={_L({key: 'Floor', localeState})}
                    title={_L({key: 'Select a floor', localeState})}
                    placeholder={_L({key: 'Search Floors', localeState})}
                    value={floor}
                    readOnly={isQrRequest}
                    selectors={selectors}
                    onChange={onChange}
                    parentFields={['blId']}
                    parents={['building']}
                    clearValue
                    dataTestId="location-floor-select"
                    ariaLabel= "Select a floor"
                  />
                  <FieldMessage />
                </div>
              )}
              {hasSpecificLocation && (
                <div>
                  <StyledFieldLabel htmlFor="Specific Location or Room">
                    Specific Location/Room
                  </StyledFieldLabel>
                  <StyledTextArea
                    id="Specific Location or Room"
                    small
                    rows={4}
                    maxLength={50}
                    onChange={onLocationChange}
                    value={location}
                    dataTestId="location-specific-location"
                  />
                </div>
              )}

              {hasEquipment && (
                <div>
                  <Select
                    parentContainer={parentEl}
                    type={PromptType.EQUIPMENT}
                    label={_L({key: 'Equipment', localeState})}
                    title={_L({key: 'Select an equipment', localeState})}
                    placeholder={_L({key: 'Search Equipment', localeState})}
                    value={equipment}
                    readOnly={false}
                    parentFields={['blId', 'flId', 'rmId']}
                    parents={['building', 'floor', 'room']}
                    selectors={selectors}
                    onChange={onChange}
                  />
                  <FieldMessage errors={errors} fieldName={'equipment'} />
                </div>
              )}
            </FieldContainer>
          </StyledContent>
        )}
      />
      <StyledNavigationButtons>
        <NavigationButtons onNextClick={onNext} />
      </StyledNavigationButtons>
    </StyledContainer>
  )
}

export default Location

Location.defaultProps = {
  isQrRequest: false
}

const StyledContainer = styled.div`
  width: 712px;
  position: relative;

  @media (max-width: 420px) {
    width: 100vw;
    height: calc(100vh - 162px);
    overflow-y: auto;
    background-color: white;
  }
`

const StyledContent = styled.div`
  margin-top: 14px;
  margin-bottom: 24px;
  box-shadow: 0 10px 40px 0 rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  background-color: #ffffff;
  padding: 2px 0 33px 0;

  @media (max-width: 420px) {
    margin: 0;
    border-radius: 0;
    width: 100vw;
    padding: 6px 0 6px 0;
  }
`

const TopFieldContainer = styled.div`
padding: 24px 50px 5px 50px;

@media (max-width: 420px) {
  padding: 0px 24px 0px 24px;
  box-sizing: border-box;
}
`

const FieldContainer = styled.div`
  padding: 18px 50px 5px 50px;

  @media (max-width: 420px) {
    padding: 18px 24px 8px 24px;
    box-sizing: border-box;
  }
`

const InputContainer = styled.div`
  padding-top: 8;
  padding-bottom: 10;
  width: 334px;
  @media (max-width: 420px) {
    width: 100%;
  }
`

const HeaderContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`

const Title = styled.h2`
  font-size: 22px;
  font-weight: 700;
  padding-bottom: 4px;
  user-select: none;
`

const Subtitle = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  color: #a4a9b7;
`

const StyledTextArea = styled.textarea`
  width: 100%;
  border-radius: 6px;
  border: 0;
  height: ${props => (props.small ? '46px' : '72px')};
  background-color: #f3f5fb;
  resize: ${props => (props.small ? 'none' : 'vertical')};
  font-size: 16px;
  font-weight: 600;
  font-family: 'Open Sans';
  padding: 14px 20px 10px 20px;
  box-sizing: border-box;
  :focus {
	   outline: 3px solid #FF9900;
	}

  @media (max-width: 1024px) {
    height: ${props => (props.small ? '46px' : '80px')};
  }

  @media (max-width: 420px) {
    height: ${props => props.small && '70px'};
  }
`

const StyledNavigationButtons = styled.div`
  @media (max-width: 420px) {
    width: 100vw;
    height: 70px;
    background-color: white;
    position: fixed;
    bottom: 0;
    padding-top: 6px;
    border-top: #13294b solid 2px;
  }
`

const StyledTitle = styled.div`
  font-size: 22px;
  font-weight: 700;
  padding-bottom: 14px;

  @media (max-width: 420px) {
    font-size: 20px;
    padding-bottom: 10px;
  }
`

const StyledFieldLabel = styled.label`
  color: #3e4044;
  font-size: 16px;
  font-weight: normal;
  padding-bottom: 2px;
`

const FieldTitleText = styled.div`
  font-size: 16px;
  color: #3e4044;
  font-weight: normal;
`

const FieldInstructions = styled.div`
  font-style: italic;
  font-weight: normal;
  font-size: 14px;
  color: #a4a9b7;
  padding-top: 2px;
`
